'use client';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';

import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  MapPin,
  Plane,
  ShieldCheck,
  Star,
  Users,
  BriefcaseBusiness,
  Car,
  Route,
  CalendarCheck,
  MessageCircle,
  ChevronDown,
  Building2,
  Compass,
  BookOpenText,
  KeyRound,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

const createWhatsAppUrl = (vehicle = '') => {
  const message = `Halo NAYVERA INDO TRANSPORT,

Saya ingin menanyakan ketersediaan sewa mobil Bogor.

DATA PEMESANAN

Nama pemesan:
Kendaraan yang diminati: ${vehicle}
Jenis sewa: Dengan Sopir / Lepas Kunci
Tanggal mulai:
Jam mulai:
Tanggal selesai:
Jam selesai:
Lokasi penjemputan:
Tujuan / rute:
Jumlah penumpang:

Mohon informasi mengenai:
• Ketersediaan kendaraan
• Harga dan pilihan paket
• Fasilitas yang didapat
• Syarat dan ketentuan
• Biaya tambahan jika ada

Terima kasih.`;

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
};

const fleet = [
  {
    name: 'Toyota Agya / Daihatsu Ayla',
    category: 'City Car',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '5 Penumpang',
    image: '/assets/images/AYLA DAN AGYA.jpg',
    features: ['Power Steering', 'AC Dingin', 'Audio Bluetooth', 'Eco Mode'],
    price: 'Rp 300.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Pilihan praktis untuk keliling Kota Hujan Bogor, wisata kuliner, dan perjalanan harian ringkas.',
  },
  {
    name: 'Toyota Avanza',
    category: 'Family MPV',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/Avanza.jpeg',
    features: ['AC Double Blower', 'Audio Sistem', 'Kabin Luas', '+1 lagi'],
    price: 'Rp 550.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Kendaraan keluarga andalan untuk trip wisata ke Puncak, Sentul, maupun aktivitas harian di Bogor.',
  },
  {
    name: 'Toyota Innova Reborn',
    category: 'Family & Business',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel / Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/REBORN.jpeg',
    features: ['AC Double Blower', 'Kabin Nyaman', 'Captain Seat opt', '+1 lagi'],
    price: 'Rp 700.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Kabin tenang dan bertenaga untuk liburan keluarga ke dataran tinggi atau kunjungan dinas di Bogor.',
  },
  {
    name: 'Toyota Innova Zenix',
    category: 'Executive MPV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Bensin / Hybrid',
    capacity: '7 Penumpang',
    image: '/assets/images/ZENIX G.webp',
    features: ['Panoramic Roof', 'Captain Seat', 'Electric Parking', '+1 lagi'],
    price: 'Rp 850.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Perjalanan mewah dan modern untuk agenda meeting corporate di Sentul atau liburan keluarga berkelas.',
  },
  {
    name: 'Toyota Fortuner',
    category: 'Premium SUV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel',
    capacity: '7 Penumpang',
    image: '/assets/images/Fortuner.jpeg',
    features: ['4x2 / 4x4', 'Leather Seat', 'Push Start Button', '+1 lagi'],
    price: 'Rp 1.600.000',
    priceLabel: '12 jam · dengan driver',
    description: 'SUV tangguh untuk menembus kontur tanjakan Puncak dan perjalanan VIP yang mengutamakan kenyamanan.',
  },
  {
    name: 'Toyota Hiace Premio',
    category: 'Premium Van',
    status: 'Tersedia',
    transmission: 'Manual',
    fuel: 'Diesel',
    capacity: '14 Penumpang',
    image: '/assets/images/Hiace-Premio.jpeg',
    features: ['High Roof', 'AC per Kepala', 'Reclining Seat', '+1 lagi'],
    price: 'Rp 1.800.000',
    priceLabel: '12 jam · dengan driver',
    description: 'Kabin lapang berkapasitas besar cocok untuk rombongan outing kantor, gathering, atau wisata keluarga besar.',
  },
];

const services = [
  {
    icon: KeyRound,
    title: 'Sewa Mobil Lepas Kunci Bogor',
    description:
      'Pilihan fleksibel bagi Anda yang membutuhkan kendaraan mandiri untuk mengeksplorasi kawasan Bogor dan sekitarnya. Konfirmasi ketersediaan unit cepat.',
  },
  {
    icon: Users,
    title: 'Sewa Mobil dengan Sopir',
    description:
      'Nikmati perjalanan tanpa lelah menembus kemacetan Puncak atau Sentul bersama pengemudi berpengalaman yang paham jalur alternatif.',
  },
  {
    icon: Plane,
    title: 'Antar Jemput Bandara',
    description:
      'Layanan penjemputan dan pengantaran tepat waktu dari Bogor menuju Bandara Soekarno-Hatta maupun Halim Perdanakusuma via Tol Jagorawi.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Transportasi Bisnis & Instansi',
    description:
      'Armada representatif untuk kebutuhan rapat dinas, event di Sentul International Convention Center (SICC), maupun kunjungan instansi di Bogor.',
  },
  {
    icon: Route,
    title: 'Perjalanan Luar Kota dari Bogor',
    description:
      'Layanan carter atau drop-off perjalanan dari Bogor menuju Jakarta, Bandung, Sukabumi, Cianjur, hingga kota-kota lainnya.',
  },
  {
    icon: Compass,
    title: 'Wisata Puncak, Sentul & Kebun Raya',
    description:
      'Eksplorasi destinasi liburan favorit Bogor seperti Kebun Raya, kawasan kuliner Pajajaran, Sentul, hingga pegunungan Puncak.',
  },
];

const useCases = [
  {
    icon: CalendarCheck,
    title: 'Wisata Kuliner & Harian Kota',
    description:
      'Solusi praktis keliling kota Bogor menikmati sajian kuliner khas tanpa pusing mencari parkir.',
  },
  {
    icon: Building2,
    title: 'Meeting & Event di Sentul',
    description:
      'Mendukung mobilitas tamu VIP dan rombongan untuk acara seminar, konser, atau gathering di kawasan Sentul.',
  },
  {
    icon: Plane,
    title: 'Drop / Pick-up Bandara',
    description:
      'Layanan antar jemput bandara langsung dari rumah atau hotel di Bogor dengan jaminan ketepatan waktu.',
  },
  {
    icon: Users,
    title: 'Gathering & Outing Kantor',
    description:
      'Tersedia Hiace dan microbus berkapasitas besar untuk menampung rombongan perusahaan dalam satu kendaraan.',
  },
  {
    icon: Star,
    title: 'Liburan Akhir Pekan di Puncak',
    description:
      'Perjalanan santai menembus jalur tanjakan Puncak tanpa kelelahan menyetir saat diberlakukan sistem ganjil-genap/buka-tutup.',
  },
  {
    icon: Route,
    title: 'Carter Antar Kota Lintas Provinsi',
    description:
      'Perjalanan jarak jauh berangkat langsung dari titik jemput Bogor via tol trans-jawa secara aman dan nyaman.',
  },
];

const areaGroups = [
  {
    title: 'Kota Bogor',
    description: 'Cakupan cepat di pusat kota Bogor.',
    places: [
      'Bogor Tengah (Kebun Raya)',
      'Bogor Timur (Pajajaran)',
      'Bogor Selatan',
      'Bogor Utara (Baranangsiang)',
      'Bogor Barat',
      'Tanah Sareal',
    ],
  },
  {
    title: 'Kabupaten Bogor & Sentul',
    description: 'Menjangkau kawasan hunian & event.',
    places: [
      'Sentul City & Babakan Madang',
      'Cibinong',
      'Ciawi',
      'Cibubur - Cileungsi',
      'Parung',
      'Dramaga',
    ],
  },
  {
    title: 'Kawasan Wisata Bogor',
    description: 'Destinasi favorit akhir pekan.',
    places: [
      'Puncak (Megamendung & Cisarua)',
      'Taman Safari Indonesia',
      'Gunung Geulis',
      'Kebun Raya Bogor',
      'JungleLand Sentul',
      'Puncak Dua',
    ],
  },
  {
    title: 'Akses Tol & Bandara',
    description: 'Konektivitas transportasi utama.',
    places: [
      'Gerbang Tol Bogor (Jagorawi)',
      'Gerbang Tol Sentul Selatan',
      'Stasiun Bogor',
      'Terminal Baranangsiang',
      'Bandara Soekarno-Hatta',
      'Bandara Halim Perdanakusuma',
    ],
  },
];

const reasons = [
  {
    icon: Car,
    title: 'Armada Prima & Bersih',
    text: 'Kondisi kendaraan rutin diservis dan dibersihkan demi performa terbaik di jalur menanjak.',
  },
  {
    icon: MessageCircle,
    title: 'Konsultasi Mudah via WhatsApp',
    text: 'Admin responsif 24 jam siap mengonfirmasi ketersediaan unit dan rekomendasi armada.',
  },
  {
    icon: MapPin,
    title: 'Driver Paham Rute Bogor & Puncak',
    text: 'Pengemudi handal yang menguasai jalan tikus/alternatif untuk menghindari macet ganjil-genap Puncak.',
  },
  {
    icon: ShieldCheck,
    title: 'Transparan & Terpercaya',
    text: 'Informasi tarif sewa, ketentuan, dan fasilitas disampaikan secara jelas sejak awal.',
  },
];

const articles = [
  {
    number: '01',
    category: 'Panduan Wisata',
    title: 'Tips Memilih Sewa Mobil Bogor untuk Liburan Akhir Pekan ke Puncak & Sentul',
    paragraphs: [
      'Bogor selalu menjadi tujuan utama wisata warga Jabodetabek berkat hawa sejuknya di kawasan Puncak serta beragam tempat nongkrong estetik di Sentul. Namun, perjalanan menuju kawasan dataran tinggi Bogor membutuhkan kondisi kendaraan yang benar-benar sehat dan bertenaga.',
      'Menggunakan jasa sewa mobil dengan sopir di Bogor sangat disarankan untuk menghindari stres akibat sistem buka-tutup jalur (one-way) atau aturan ganjil-genap di Puncak. Pengemudi kami yang berpengalaman menguasai rute-rute alternatif seperti jalur simpang Ciawi atau Katulampa.',
      'Rencanakan jadwal keberangkatan Anda lebih awal dan konsultasikan rute tujuan bersama tim NAYVERA agar perjalanan liburan keluarga terasa santai dan efisien.',
    ],
  },
  {
    number: '02',
    category: 'Solusi Korporat',
    title: 'Keuntungan Sewa Mobil di Bogor untuk Agenda Rapat Dinas & Event Sentul',
    paragraphs: [
      'Sentul City dan pusat kota Bogor sering kali menjadi lokasi favorit gelaran konferensi, pelatihan korporat, maupun pameran berskala besar di SICC (Sentul International Convention Center).',
      'NAYVERA INDO TRANSPORT menghadirkan pilihan armada eksklusif seperti Toyota Innova Zenix dan Fortuner yang memberikan rasa nyaman dan profesionalisme tinggi saat mengantar para eksekutif maupun tamu VIP.',
      'Layanan kami didukung pengemudi berpakaian rapi dan disiplin waktu, memastikan agenda rapat dinas maupun kunjungan kerja Anda di Bogor berjalan tepat waktu.',
    ],
  },
  {
    number: '03',
    category: 'Lepas Kunci vs Sopir',
    title: 'Sewa Mobil Lepas Kunci atau Dengan Driver di Bogor: Mana yang Tepat?',
    paragraphs: [
      'Menyewa mobil lepas kunci di Bogor cocok bagi warga lokal atau pelanggan yang sudah terbiasa menavigasi jalur lalu lintas kota dan membutuhkan kebebasan privasi penuh.',
      'Di sisi lain, opsi dengan pengemudi menjadi pilihan favorit bagi wisatawan luar kota yang ingin menikmati keindahan pemandangan tanpa perlu capek menghadapi kemacetan akhir pekan di ruas jalan utama Bogor-Puncak.',
      'Apapun pilihan Anda, pastikan untuk menanyakan syarat administrasi sewa harian, durasi waktu pemakaian, serta jangkauan area operasional sebelum melakukan reservasi.',
    ],
  },
  {
    number: '04',
    category: 'Airport Transfer',
    title: 'Panduan Antar Jemput Bandara dari Bogor via Tol Jagorawi',
    paragraphs: [
      'Perjalanan darat dari Bogor menuju Bandara Soekarno-Hatta (Soetta) atau Halim Perdanakusuma melintasi jalan tol Jagorawi kini semakin cepat dan mudah.',
      'Layanan antar jemput bandara dari NAYVERA memberikan kepastian jam penjemputan dari rumah atau hotel di Bogor sehingga Anda tidak perlu khawatir terlambat mengejar penerbangan.',
      'Cukup informasikan jadwal keberangkatan atau nomor penerbangan kedatangan Anda, dan driver kami siap menunggu di lokasi penjemputan dengan armada yang bersih.',
    ],
  },
  {
    number: '05',
    category: 'Rekomendasi Rombongan',
    title: 'Sewa Hiace Premio di Bogor untuk Outing Perusahaan & Family Gathering',
    paragraphs: [
      'Acara gathering keluarga besar atau outing kantor ke destinasi wisata alam di Bogor akan terasa lebih hangat jika seluruh peserta dapat bepergian bersama dalam satu kabin.',
      'Toyota Hiace Premio yang kami sediakan memiliki konfigurasi kursi yang lega, AC dingin di setiap baris, serta suspensi lembut yang menjaga kenyamanan selama menempuh rute pegunungan.',
      'Konsultasikan jumlah rombongan Anda dengan admin kami agar penyiapan armada microbus berkapasitas besar dapat dilakukan dengan maksimal.',
    ],
  },
  {
    number: '06',
    category: 'Panduan Booking',
    title: 'Cara Mudah Pemesanan Rental Mobil Bogor di NAYVERA INDO TRANSPORT',
    paragraphs: [
      'Proses booking rental mobil di NAYVERA dirancang sangat ringkas via WhatsApp tanpa perlu repot mengisi formulir pendaftaran yang panjang.',
      'Kirimkan saja tanggal pemakaian, jam penjemputan, lokasi jelas di wilayah Bogor, rute yang ingin dituju, serta jenis armada yang diinginkan.',
      'Admin kami akan segera membalas rincian harga transparan, opsi ketersediaan unit, serta syarat ketentuan sewa secara jelas.',
    ],
  },
];

const faqs = [
  {
    question: 'Berapa tarif sewa mobil di Bogor bersama NAYVERA?',
    answer:
      'Tarif sewa sangat terjangkau mulai dari 300 ribuan tergantung jenis armada yang dipilih, durasi sewa, serta pilihan lepas kunci atau menggunakan pengemudi.',
  },
  {
    question: 'Apakah tersedia sewa mobil Bogor lepas kunci?',
    answer:
      'Ya, kami menyediakan layanan sewa lepas kunci untuk tipe unit tertentu setelah melalui proses verifikasi persyaratan dokumen identitas demi keamanan.',
  },
  {
    question: 'Bagaimana jika saya ingin sewa mobil ke area Puncak?',
    answer:
      'Sangat disarankan menggunakan jasa driver kami yang sudah berpengalaman menaklukkan tanjakan Puncak dan mengetahui waktu-waktu penutupan arus (one-way).',
  },
  {
    question: 'Wilayah mana saja di Bogor yang masuk dalam area layanan?',
    answer:
      'Kami melayani seluruh area Kota Bogor, Sentul City, Cibinong, Ciawi, Puncak Cisarua, Parung, hingga penjemputan di stasiun dan hotel.',
  },
  {
    question: 'Apakah bisa mengantar jemput dari Bandara Soekarno-Hatta ke Bogor?',
    answer:
      'Tentu saja. Kami melayani rute antar-jemput bandara Soekarno-Hatta (Tangerang) maupun Halim Perdanakusuma (Jakarta) menuju wilayah Bogor.',
  },
  {
    question: 'Apakah mobil bisa digunakan untuk perjalanan luar kota Bogor?',
    answer:
      'Bisa. Mobil dapat disewa untuk perjalanan jarak jauh menuju Jakarta, Bandung, Sukabumi, Cianjur, Jawa Tengah, atau lokasi lainnya.',
  },
  {
    question: 'Bagaimana cara reservasi unit kendaraan?',
    answer:
      'Cukup klik tombol WhatsApp di halaman ini, kirimkan detail jadwal dan alamat jemput Anda, lalu admin kami akan mengonfirmasi ketersediaannya.',
  },
  {
    question: 'Apakah harga sudah All-In dengan tol, parkir, dan BBM?',
    answer:
      'Bergantung pada jenis paket sewa yang Anda pilih. Detail apakah paket sewa termasuk BBM, tol, dan parkir akan diinfokan secara transparan oleh admin.',
  },
];

export default function SewaMobilBogorPage() {
  const whatsappUrl = createWhatsAppUrl();

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#07111f]">
        <div className="absolute inset-0">
          <img
            src="/assets/images/AYLA DAN AGYA.jpg"
            alt="Sewa mobil Bogor NAYVERA INDO TRANSPORT"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07111f] via-[#07111f]/95 to-[#07111f]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07111f] via-transparent to-transparent" />
        </div>

        <div className="relative mx-auto grid min-h-[680px] max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-[1.15fr_.85fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-2 text-sm font-medium text-[#e4c45a]">
              <MapPin size={16} />
              Sewa Mobil Bogor & Sekitarnya
            </div>

            <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sewa Mobil Bogor
              <span className="mt-2 block text-[#d4af37]">
                Mulai dari 300 Ribuan Terpercaya
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              NAYVERA INDO TRANSPORT menyediakan solusi rental mobil profesional
              di Bogor untuk kebutuhan wisata Puncak & Sentul, harian keluarga,
              agenda bisnis instansi, antar-jemput bandara, hingga carter luar kota.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={19} />
                Konsultasi & Booking WhatsApp
                <ArrowRight size={18} />
              </a>

              <a
                href="#armada"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 font-semibold text-white backdrop-blur transition hover:border-[#d4af37]/60 hover:bg-white/10"
              >
                Lihat Pilihan Armada
              </a>
            </div>

            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ['300 Ribuan', 'Tarif terjangkau*'],
                ['Lepas Kunci', 'Opsi fleksibel'],
                ['Dengan Sopir', 'Driver berpengalaman'],
                ['Bogor & Sentul', 'Cakupan luas'],
              ].map(([value, label]) => (
                <div
                  key={label}
                  className="border-l border-[#d4af37]/50 pl-4"
                >
                  <div className="font-bold text-[#e4c45a]">{value}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-400">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
                  <Car size={25} />
                </div>

                <div>
                  <p className="font-bold text-white">Rencana Perjalanan di Bogor?</p>
                  <p className="text-sm text-slate-400">
                    Konsultasikan kebutuhan Anda sekarang
                  </p>
                </div>
              </div>

              <div className="mt-7 space-y-4">
                {[
                  'Tentukan tanggal dan durasi sewa',
                  'Pilih armada sesuai jumlah penumpang',
                  'Tentukan titik jemput di Bogor',
                  'Konfirmasikan harga & fasilitas',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2
                      size={19}
                      className="mt-0.5 shrink-0 text-[#d4af37]"
                    />
                    <p className="text-sm leading-6 text-slate-300">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-5 py-3.5 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={18} />
                Tanyakan Ketersediaan Unit
              </a>

              <p className="mt-4 text-center text-xs leading-5 text-slate-400">
                Respon cepat via WhatsApp oleh tim NAYVERA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
          <div>
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Pusat Rental Mobil Bogor
            </p>

            <h2 className="text-3xl font-bold leading-tight text-[#07111f] sm:text-4xl">
              Solusi Transportasi Terpercaya di
              <span className="text-[#b89528]"> Kota & Kabupaten Bogor</span>
            </h2>

            <p className="mt-6 text-base leading-8 text-slate-600">
              Mencari layanan rental mobil yang aman, bersih, dan nyaman di Bogor? 
              NAYVERA INDO TRANSPORT hadir memberikan kemudahan transportasi bagi Anda 
              yang ingin berlibur ke kawasan Puncak, melakukan kegiatan bisnis di Sentul, 
              maupun mobilitas harian di pusat Kota Hujan.
            </p>

            <p className="mt-4 text-base leading-8 text-slate-600">
              Kami menawarkan pilihan sewa mobil lepas kunci bagi yang ingin mengemudi sendiri, 
              atau opsi dengan pengemudi profesional yang siap memandu perjalanan Anda menembus 
              jalur-jalur pegunungan Bogor dengan aman.
            </p>
          </div>

          <div className="rounded-3xl bg-[#07111f] p-8 shadow-xl">
            <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
              <ShieldCheck size={25} />
            </div>

            <h3 className="text-2xl font-bold text-white">
              Keunggulan Layanan NAYVERA
            </h3>

            <div className="mt-7 space-y-4">
              {[
                'Armada prima & bertenaga di tanjakan',
                'Opsi lepas kunci atau dengan sopir',
                'Cakupan luas Bogor, Sentul & Puncak',
                'Tarif transparan tanpa biaya tersembunyi',
                'Layanan respon cepat WhatsApp 24 jam',
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <CheckCircle2
                    size={20}
                    className="mt-0.5 shrink-0 text-[#d4af37]"
                  />
                  <span className="text-sm leading-6 text-slate-300">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#07111f] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Layanan NAYVERA di Bogor
            </p>

            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Pilihan Jenis Layanan Rental Mobil
              <span className="text-[#d4af37]"> Sesuai Kebutuhan</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Sesuaikan model layanan kendaraan dengan kebutuhan perjalanan Anda di Bogor.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => {
              const Icon = service.icon;

              return (
                <article
                  key={service.title}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/60 hover:bg-white/[0.07]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4af37]/10 text-[#e4c45a] transition group-hover:bg-[#d4af37] group-hover:text-[#07111f]">
                    <Icon size={23} />
                  </div>

                  <h3 className="mt-6 text-xl font-bold text-white">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-400">
                    {service.description}
                  </p>

                  <a
                    href={createWhatsAppUrl(service.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                  >
                    Konsultasikan layanan ini
                    <ArrowRight size={16} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Agenda Perjalanan
            </p>

            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Kendaraan Andal untuk
              <span className="text-[#b89528]"> Berbagai Keperluan</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#d4af37]/50 hover:shadow-lg"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={21} />
                  </div>

                  <h3 className="mt-5 text-lg font-bold text-[#07111f]">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET / ARMADA (Desain Baru Sesuai Referensi) */}
      <section id="armada" className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
                Katalog Armada
              </p>

              <h2 className="text-3xl font-bold text-[#07111f] sm:text-4xl">
                Pilihan Armada Sewa Mobil
                <span className="text-[#b89528]"> Bogor</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-600">
                Berikut adalah deretan armada terbaik kami yang siap disewa untuk menemani 
                perjalanan Anda di Bogor, Sentul, Puncak, dan sekitarnya.
              </p>
            </div>

            <a
              href="/fleet-armada-page"
              className="inline-flex items-center gap-2 font-semibold text-[#07111f] transition hover:text-[#b89528]"
            >
              Lihat Armada Lengkap
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {fleet.map((car) => (
              <article
                key={car.name}
                className="group flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/70 hover:shadow-xl"
              >
                <div>
                  <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100">
                    <img
                      src={car.image}
                      alt={`${car.name} untuk sewa mobil Bogor NAYVERA`}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                      {car.status}
                    </div>

                    <div className="absolute right-3 top-3 rounded-full bg-[#d4af37] px-3 py-1 text-xs font-bold text-[#07111f] shadow-sm">
                      {car.category}
                    </div>

                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-lg bg-black/60 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Users size={13} className="text-[#d4af37]" />
                      {car.capacity}
                    </div>
                  </div>

                  <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#b89528]">
                    {car.category}
                  </p>

                  <h3 className="mt-1 text-xl font-bold text-[#07111f]">
                    {car.name}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⚙️ {car.transmission}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⛽ {car.fuel}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {car.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600"
                      >
                        ✨ {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 border-t border-slate-100 pt-4">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Mulai dari</p>
                      <p className="text-lg font-black text-[#07111f]">{car.price}</p>
                      <p className="text-[11px] text-slate-400">{car.priceLabel}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-xl bg-[#d4af37] px-4 py-2 text-center text-xs font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
                      >
                        Booking
                      </a>
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1 rounded-xl border border-emerald-500/40 bg-emerald-50/50 px-3 py-1.5 text-center text-xs font-bold text-emerald-600 transition hover:bg-emerald-500/10"
                      >
                        <MessageCircle size={13} />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-[#0b1726] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Cakupan Wilayah
            </p>

            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              Area Layanan Rental Mobil
              <span className="text-[#d4af37]"> Bogor & Sekitarnya</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Kami melayani penjemputan di seluruh wilayah Kota Bogor, Kabupaten Bogor, 
              Sentul City, hingga kawasan wisata Puncak.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {areaGroups.map((area) => (
              <article
                key={area.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
              >
                <div className="flex items-center gap-2 text-[#e4c45a]">
                  <MapPin size={18} />
                  <h3 className="font-bold text-white">{area.title}</h3>
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-400">
                  {area.description}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {area.places.map((place) => (
                    <li
                      key={place}
                      className="border-b border-white/5 pb-2 text-sm text-slate-400 last:border-0"
                    >
                      {place}
                    </li>
                  ))}
                </ul>

                <a
                  href={createWhatsAppUrl(`Perjalanan area ${area.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                >
                  Tanyakan area ini
                  <ArrowRight size={15} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT */}
      <section className="overflow-hidden bg-[#07111f]">
        <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-[520px]">
            <img
              src="/assets/images/Armada Hiace Nayvera.jpeg"
              alt="Armada NAYVERA untuk perjalanan bandara dari Bogor"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/50 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#07111f]" />
          </div>

          <div className="flex items-center px-6 py-20 lg:px-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
                Airport Transfer & Drop Off
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl">
                Antar Jemput Bandara
                <span className="text-[#d4af37]"> Nyaman dari Bogor</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-300">
                Butuh transportasi cepat dan nyaman menuju Bandara Soekarno-Hatta 
                atau Halim Perdanakusuma dari Bogor? NAYVERA siap melayani perjalanan Anda 
                dengan unit terawat dan pengemudi profesional via akses tol Jagorawi.
              </p>

              <div className="mt-8">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
                >
                  <MessageCircle size={18} />
                  Pesan Antar Jemput Bandara
                  <ArrowRight size={17} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UNIQUE SEO ARTICLES SECTION */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Wawasan & Panduan Sewa Mobil
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Artikel & Tips Seputar
              <span className="text-[#b89528]"> Rental Mobil di Bogor</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Informasi lengkap untuk membantu merencanakan liburan, agenda bisnis, dan perjalanan keluarga Anda di Bogor.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {articles.map((art) => (
              <article
                key={art.number}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:border-[#d4af37]/60 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#07111f] px-3.5 py-1 text-xs font-bold text-[#e4c45a]">
                    {art.category}
                  </span>
                  <span className="text-2xl font-black text-slate-300">
                    {art.number}
                  </span>
                </div>

                <h3 className="mt-5 text-xl font-bold text-[#07111f]">
                  {art.title}
                </h3>

                <div className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                  {art.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Kenapa Memilih Kami
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Alasan Percayakan Perjalanan Anda
              <span className="text-[#b89528]"> Kepada NAYVERA</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {reasons.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.title}
                  className="rounded-2xl border border-slate-200 p-6 transition hover:border-[#b89528]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-bold text-[#07111f]">{r.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Pertanyaan yang Sering Diajukan
            </h2>
            <p className="mt-4 text-slate-600">
              Temukan jawaban seputar layanan sewa mobil Bogor bersama NAYVERA.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition open:border-[#d4af37]"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-[#07111f]">
                  {faq.question}
                  <ChevronDown size={18} className="transition group-open:rotate-180 text-[#b89528]" />
                </summary>
                <p className="mt-4 text-sm leading-7 text-slate-600">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FOOTER BANNER */}
      <section className="bg-[#07111f] py-20 text-white">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
            Siap Berangkat ke Bogor?
          </p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Pesan Kendaraan Anda Sekarang di NAYVERA
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Dapatkan pelayanan cepat, ramah, dan transparan untuk setiap kebutuhan perjalanan Anda.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-8 py-4 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
            >
              <MessageCircle size={20} />
              Hubungi WhatsApp Sekarang
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}